import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getSiteContent } from "../services/api";
import { loadSection } from "../services/contentSource";
import teamFallback from "../data/home/team.json";

interface BusinessCardData {
  personid: string;
  firstname: string;
  middlename: string;
  lastname: string;
  surname: string;
  gender: string;
  occupation: string;
  countrycode: string;
  phonenum: string;
  email: string;
  addresstype: string;
  addressname: string;
  addrline1: string;
  addrline2: string;
  city: string;
  state: string;
  postalcode: string;
  Countryname: string;
  area: string;
  facebooklink: string;
  instalink: string;
  linkdlnlink: string;
  businessname: string;
  businesscategory: string;
  website: string;
  businessdescription: string;
  logo: string;
  aboutme: string;
  productservices: string;
  profilepicture: string;
  fullName: string;
  fullAddress: string;
}

const NETWORKING_API_URL = "https://networking.cognisun.net:4015";
const NETWORKING_BASE_URL = "https://networking.cognisun.net";

const BusinessCard: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const [cardData, setCardData] = useState<BusinessCardData | null>(null);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSection("team", teamFallback as any).then(({ data: main }) => setTeamMembers(main?.members || []));
  }, []);

  useEffect(() => {
    const fetchCardData = async () => {
      try {
        const response = await fetch(
          `${NETWORKING_API_URL}/member/getMemberDetails`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ personid: uuid }),
          }
        );

        if (!response.ok) {
          throw new Error("Business card not found");
        }

        const result = await response.json();

        if (result.success && result.data && result.data.length > 0) {
          setCardData(result.data[0]);
        } else {
          throw new Error("Business card not found");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load business card");
      } finally {
        setLoading(false);
      }
    };

    if (uuid) {
      fetchCardData();
    }
  }, [uuid]);

  // Helper to get display name
  const getDisplayName = () => {
    if (cardData?.fullName) return cardData.fullName.trim();
    const parts = [cardData?.firstname, cardData?.middlename, cardData?.lastname].filter(Boolean);
    return parts.join(" ").trim() || "Unknown";
  };

  // Helper to get profile image
  const getProfileImage = () => {
    if (cardData?.profilepicture) {
      return `${NETWORKING_API_URL}/${cardData.profilepicture}`;
    }
    if (cardData?.logo) {
      return `${NETWORKING_BASE_URL}/${cardData.logo}`;
    }
    return null;
  };

  // Helper to get title/designation
  const getTitle = () => {
    return cardData?.occupation || "";
  };

  // Helper to get company
  const getCompany = () => {
    return "MindSyncX Labs LLP";
  };

  // Helper to get email from team data by matching ID
  const getTeamEmail = () => {
    const teamMember = teamMembers.find((member: any) => member.id === uuid);
    return teamMember?.email || cardData?.email || "";
  };

  // Fixed company website
  const getWebsite = () => {
    return "http://mindsyncx.com";
  };

  // Helper to get phone
  const getPhone = () => {
    if (cardData?.phonenum) {
      return `${cardData.countrycode || ""}${cardData.phonenum}`;
    }
    return "";
  };

  // Helper to get bio
  const getBio = () => {
    return cardData?.aboutme || cardData?.businessdescription || "";
  };

  // Helper to get business category
  const getBusinessCategory = () => {
    return cardData?.businesscategory || "";
  };

  // Helper to get full address
  const getFullAddress = () => {
    if (cardData?.fullAddress) return cardData.fullAddress;
    const parts = [
      cardData?.addrline1,
      cardData?.addrline2,
      cardData?.area,
      cardData?.city,
      cardData?.state,
      cardData?.postalcode,
      cardData?.Countryname,
    ].filter(Boolean);
    return parts.join(", ");
  };

  // Helper to get social links
  const getSocialLinks = () => {
    return {
      linkedin: cardData?.linkdlnlink || "",
      facebook: cardData?.facebooklink || "",
      instagram: cardData?.instalink || "",
    };
  };

  // Generate vCard for download
  const downloadVCard = () => {
    if (!cardData) return;

    const firstName = cardData.firstname?.trim() || "";
    const middleName = cardData.middlename?.trim() || "";
    const lastName = cardData.lastname?.trim() || "";
    const profileImageUrl = getProfileImage();

    // Build vCard with CRLF line endings (required by iOS)
    const lines = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `N:${lastName};${firstName};${middleName};;`,
      `FN:${firstName} ${lastName}`.trim(),
      getTitle() ? `TITLE:${getTitle()}` : "",
      getCompany() ? `ORG:${getCompany()}` : "",
      getPhone() ? `TEL;TYPE=CELL:${getPhone()}` : "",
      getTeamEmail() ? `EMAIL:${getTeamEmail()}` : "",
      getWebsite() ? `URL:${getWebsite()}` : "",
      (cardData.city || cardData.state || cardData.Countryname)
        ? `ADR;TYPE=WORK:;;${cardData.addrline1 || ""};${cardData.city || ""};${cardData.state || ""};${cardData.postalcode || ""};${cardData.Countryname || ""}`
        : "",
      profileImageUrl ? `PHOTO;VALUE=URI:${profileImageUrl}` : "",
      "END:VCARD"
    ].filter(line => line !== "");

    // Join with CRLF (required by vCard spec for iOS compatibility)
    const vcard = lines.join("\r\n");

    const blob = new Blob([vcard], { type: "text/vcard;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${getDisplayName().replace(/\s+/g, "_")}.vcf`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <>
        <link rel="stylesheet" href="/assets/css/master-business-card.css" />
        <div className="business-card-page">
          <div className="business-card-container">
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error || !cardData) {
    return (
      <>
        <link rel="stylesheet" href="/assets/css/master-business-card.css" />
        <div className="business-card-page">
          <div className="business-card-container">
            <div className="error-state">
              <div className="error-icon">!</div>
              <h2>Business Card Not Found</h2>
              <p>{error || "The requested business card does not exist."}</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  const socialLinks = getSocialLinks();

  return (
    <>
      <link rel="stylesheet" href="/assets/css/master-business-card.css" />
      <div className="business-card-page">
        <div className="business-card-container">
          <div className="business-card">
            {/* Header with gradient */}
            <div className="card-header">
              <div className="header-pattern"></div>
            </div>

            {/* Profile Section */}
            <div className="profile-section">
              <div className="profile-image-wrapper">
                {getProfileImage() ? (
                  <img
                    src={getProfileImage()!}
                    alt={getDisplayName()}
                    className="profile-image" loading="lazy" decoding="async" />
                ) : (
                  <div className="profile-placeholder">
                    {getDisplayName().charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <h1 className="profile-name">{getDisplayName()}</h1>
              {getTitle() && <p className="profile-title">{getTitle()}</p>}
              {getCompany() && (
                <p className="profile-company">
                  <span className="company-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                      <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
                    </svg>
                  </span>
                  {getCompany()}
                </p>
              )}
              {getBusinessCategory() && (
                <p className="profile-category">{getBusinessCategory()}</p>
              )}
            </div>

            {/* Bio Section */}
            {getBio() && (
              <div className="bio-section">
                <p className="bio-text">{getBio()}</p>
              </div>
            )}

            {/* Contact Info */}
            <div className="contact-section">
              {getPhone() && (
                <a href={`tel:${getPhone()}`} className="contact-item">
                  <div className="contact-icon phone-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                    </svg>
                  </div>
                  <div className="contact-details">
                    <span className="contact-label">Phone</span>
                    <span className="contact-value">{getPhone()}</span>
                  </div>
                </a>
              )}

              {getTeamEmail() && (
                <a href={`mailto:${getTeamEmail()}`} className="contact-item">
                  <div className="contact-icon email-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                    </svg>
                  </div>
                  <div className="contact-details">
                    <span className="contact-label">Email</span>
                    <span className="contact-value">{getTeamEmail()}</span>
                  </div>
                </a>
              )}

              <a
                href={getWebsite()}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-item"
              >
                <div className="contact-icon website-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                  </svg>
                </div>
                <div className="contact-details">
                  <span className="contact-label">Website</span>
                  <span className="contact-value">{getWebsite()}</span>
                </div>
              </a>

              {getFullAddress() && (
                <div className="contact-item">
                  <div className="contact-icon location-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                    </svg>
                  </div>
                  <div className="contact-details">
                    <span className="contact-label">Address</span>
                    <span className="contact-value">{getFullAddress()}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Social Links */}
            {(socialLinks.linkedin || socialLinks.facebook || socialLinks.instagram) && (
              <div className="social-section">
                {socialLinks.linkedin && (
                  <a
                    href={socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link linkedin"
                    aria-label="LinkedIn"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                    </svg>
                  </a>
                )}
                {socialLinks.facebook && (
                  <a
                    href={socialLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link facebook"
                    aria-label="Facebook"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z" />
                    </svg>
                  </a>
                )}
                {socialLinks.instagram && (
                  <a
                    href={socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link instagram"
                    aria-label="Instagram"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z" />
                    </svg>
                  </a>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="action-section">
              <button onClick={downloadVCard} className="action-btn save-contact">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2v9.67z" />
                </svg>
                Save Contact
              </button>
            </div>

            {/* WhatsApp Button */}
            {getPhone() && (
              <div className="action-section" style={{ paddingTop: 0 }}>
                <a
                  href={`https://wa.me/${getPhone().replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="action-btn whatsapp-btn"
                  style={{ textDecoration: 'none' }}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  Chat on WhatsApp
                </a>
              </div>
            )}

            {/* Footer Branding */}
            <div className="card-footer">
              <a href="https://mindsyncx.com" target="_blank" rel="noopener noreferrer">
                <img
                  src="/assets/imgs/logo/logo.svg"
                  alt="MindSyncX"
                  className="footer-logo" loading="lazy" decoding="async" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BusinessCard;
